/** Rotas do app principal (Home) — paths canônicos na URL. */

export type HomeActiveTab =
  | "dashboard"
  | "estoque"
  | "compras"
  | "desperdicios"
  | "despesas"
  | "produtos"
  | "insumos"
  | "vendas"
  | "financeiro"
  | "fornecedores"
  | "conferencia"
  | "marketing"
  | "parceiros"
  | "config";

/** Subabas do Financeiro na URL `/financeiro/...`. */
export type FinanceiroSubTab = "vendas" | "despesas" | "compras" | "outros";

/** Subabas da área Estoque: `/estoque` vs `/entradas`. */
export type EstoqueSubTab = "estoque" | "entradas";

/** Subabas de Configurações: `/config`, `/config/usuarios`, `/config/senha`. */
export type ConfigSubTab = "geral" | "usuarios" | "senha";

export const DEFAULT_FINANCEIRO_SUB: FinanceiroSubTab = "vendas";
export const DEFAULT_CONFIG_SUB: ConfigSubTab = "geral";

const TAB_ORDER_FALLBACK = [
  "dashboard",
  "estoque",
  "compras",
  "desperdicios",
  "financeiro",
  "fornecedores",
  "conferencia",
  "marketing",
  "parceiros",
  "produtos",
  "insumos",
] as const;

const FINANCEIRO_PATH_SEGMENTS: readonly FinanceiroSubTab[] = ["vendas", "despesas", "compras", "outros"];

export function normalizeAppPath(path: string): string {
  if (!path || path === "/") return "/";
  return path.replace(/\/+$/, "") || "/";
}

export type PathForTabOptions = {
  financeiroSubTab?: FinanceiroSubTab;
  estoqueSubTab?: EstoqueSubTab;
  configSubTab?: ConfigSubTab;
};

export function pathForConfigSubTab(sub: ConfigSubTab = DEFAULT_CONFIG_SUB): string {
  switch (sub) {
    case "usuarios":
      return "/config/usuarios";
    case "senha":
      return "/config/senha";
    default:
      return "/config";
  }
}

/**
 * Path canônico para a aba e subestado (Financeiro / Estoque).
 */
export function pathForTab(tab: HomeActiveTab, opts?: PathForTabOptions): string {
  const fin = opts?.financeiroSubTab ?? DEFAULT_FINANCEIRO_SUB;
  const est = opts?.estoqueSubTab ?? "estoque";

  switch (tab) {
    case "dashboard":
      return "/dashboard";
    case "estoque":
      return est === "entradas" ? "/entradas" : "/estoque";
    case "compras":
      return "/reposicao";
    case "desperdicios":
      return "/desperdicios";
    case "financeiro":
      return `/financeiro/${fin}`;
    case "vendas":
      return "/financeiro/vendas";
    case "despesas":
      return "/financeiro/despesas";
    case "fornecedores":
      return "/pagamentos";
    case "conferencia":
      return "/conferencia";
    case "marketing":
      return "/marketing";
    case "parceiros":
      return "/cadastros/parceiros";
    case "produtos":
      return "/itens/produtos";
    case "insumos":
      return "/itens/insumos";
    case "config":
      return pathForConfigSubTab(opts?.configSubTab ?? DEFAULT_CONFIG_SUB);
    default:
      return "/dashboard";
  }
}

export function getFallbackTab(canViewTab: (name: string) => boolean): HomeActiveTab {
  for (const t of TAB_ORDER_FALLBACK) {
    if (canViewTab(t as HomeActiveTab)) return t as HomeActiveTab;
  }
  if (canViewTab("financeiro")) return "financeiro";
  if (canViewTab("itens")) return "produtos";
  return "estoque";
}

export type TabFromPathResult = {
  tab: HomeActiveTab;
  openItensSubmenu: boolean;
  openCadastrosSubmenu: boolean;
  financeiroSubTab?: FinanceiroSubTab;
  estoqueSubTab?: EstoqueSubTab;
  configSubTab?: ConfigSubTab;
};

function isFinanceiroSubSegment(s: string): s is FinanceiroSubTab {
  return (FINANCEIRO_PATH_SEGMENTS as readonly string[]).includes(s);
}

/**
 * Interpreta o pathname (já normalizado) e devolve a aba correspondente.
 * `/financeiro` sem subrota deve ser tratado no roteador com Redirect.
 */
export function tabFromPath(path: string, canViewTab: (name: string) => boolean): TabFromPathResult | null {
  const p = normalizeAppPath(path);

  if (p === "/" || p === "/dashboard") {
    return { tab: "dashboard", openItensSubmenu: false, openCadastrosSubmenu: false };
  }
  if (p === "/entradas") {
    return { tab: "estoque", openItensSubmenu: false, openCadastrosSubmenu: false, estoqueSubTab: "entradas" };
  }
  if (p === "/estoque") {
    return { tab: "estoque", openItensSubmenu: false, openCadastrosSubmenu: false, estoqueSubTab: "estoque" };
  }
  if (p === "/reposicao" || p === "/compras") {
    return { tab: "compras", openItensSubmenu: false, openCadastrosSubmenu: false };
  }
  if (p === "/desperdicios") {
    return { tab: "desperdicios", openItensSubmenu: false, openCadastrosSubmenu: false };
  }
  if (p === "/financeiro") {
    return null;
  }
  if (p.startsWith("/financeiro/")) {
    const seg = p.slice("/financeiro/".length).split("/")[0] ?? "";
    if (!isFinanceiroSubSegment(seg)) return null;
    return { tab: "financeiro", openItensSubmenu: false, openCadastrosSubmenu: false, financeiroSubTab: seg };
  }
  if (p === "/pagamentos" || p === "/fornecedores") {
    return { tab: "fornecedores", openItensSubmenu: false, openCadastrosSubmenu: false };
  }
  if (p === "/conferencia") {
    return { tab: "conferencia", openItensSubmenu: false, openCadastrosSubmenu: false };
  }
  if (p === "/marketing") {
    return { tab: "marketing", openItensSubmenu: false, openCadastrosSubmenu: false };
  }
  if (p === "/cadastros/parceiros") {
    return { tab: "parceiros", openItensSubmenu: false, openCadastrosSubmenu: true };
  }
  if (p === "/cadastros") {
    if (canViewTab("parceiros")) {
      return { tab: "parceiros", openItensSubmenu: false, openCadastrosSubmenu: true };
    }
    return null;
  }
  if (p === "/config" || p === "/config/usuarios" || p === "/config/senha") {
    let configSubTab: ConfigSubTab = "geral";
    if (p === "/config/usuarios") {
      configSubTab = "usuarios";
    } else if (p === "/config/senha") {
      configSubTab = "senha";
    }
    return {
      tab: "config",
      openItensSubmenu: false,
      openCadastrosSubmenu: false,
      configSubTab,
    };
  }

  if (p === "/itens/produtos/novo" || /^\/itens\/produtos\/\d+\/editar$/.test(p)) {
    return { tab: "produtos", openItensSubmenu: true, openCadastrosSubmenu: false };
  }
  if (p === "/meu-perfil") {
    return { tab: "config", openItensSubmenu: false, openCadastrosSubmenu: false, configSubTab: "geral" };
  }
  if (p === "/itens/produtos") {
    return { tab: "produtos", openItensSubmenu: true, openCadastrosSubmenu: false };
  }
  if (p === "/itens/insumos") {
    return { tab: "insumos", openItensSubmenu: true, openCadastrosSubmenu: false };
  }
  if (p === "/itens") {
    if (canViewTab("produtos")) {
      return { tab: "produtos", openItensSubmenu: true, openCadastrosSubmenu: false };
    }
    if (canViewTab("insumos")) {
      return { tab: "insumos", openItensSubmenu: true, openCadastrosSubmenu: false };
    }
    return null;
  }

  return null;
}

export type ProdutoFormPath =
  | { mode: "novo" }
  | { mode: "editar"; id: number };

export function parseProdutoFormPath(path: string): ProdutoFormPath | null {
  const p = normalizeAppPath(path);
  if (p === "/itens/produtos/novo") {
    return { mode: "novo" };
  }
  const m = p.match(/^\/itens\/produtos\/(\d+)\/editar$/);
  if (m) {
    const id = parseInt(m[1], 10);
    if (id > 0) return { mode: "editar", id };
  }
  return null;
}

export function isMeuPerfilPath(path: string): boolean {
  return normalizeAppPath(path) === "/meu-perfil";
}
